import { FC, useState, useEffect, useCallback } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { ArrowRight, MoreHorizontal } from 'react-feather'

import {
  Spin,
  Button,
  PopOver,
  useAssignment,
  AcceptSubmissionModal,
} from '@pepper/ui'

import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { getDocsContent } from '@pepper/utils/src/api/editor/editor'
import noop from '@pepper/utils/src/function/noop'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { FieldKeys } from '../../AssignmentDetails'

import SecureFiles from 'assets/images/undraw_secure_files_re_6vdh.svg'
import styles from './_.module.css'

interface Props {
  assignmentId: string
  data: Assignment
  projectBrief: FieldKeys[]
  assignmentBrief: FieldKeys[]
}

const Content: FC<Props> = ({ assignmentId, data }) => {
  const companyId = useSelectedCompanyId()

  const [editorLoading, setEditorLoading] = useState(true)
  const [editorContent, setEditorContent] = useState<string | null>(null)
  const [editorContentText, setEditorContentText] = useState<string | null>(
    null
  )

  //state for revamp/rework popover button
  const [shouldShowReworkRejectMenu, setShouldShowReworkRejectMenu] =
    useState(false)
  const acceptAssignmentModal = useModal(AcceptSubmissionModal)
  const declineAssignmentModal = useModal('decline-assignment-submission')
  const reworkConfirmationModal = useModal('rework-confirmation')

  const getDocData = useCallback(async () => {
    if (assignmentId) {
      const res = await getDocsContent({
        id: assignmentId,
      })
      setEditorContent(res?.data?.content?.html)
      setEditorContentText(res?.data?.content?.text)
      setEditorLoading(false)
    }
  }, [assignmentId])

  useEffect(() => {
    assignmentId && getDocData()
  }, [assignmentId, getDocData])

  const {
    data: assignment,
    isLoading: isAssignmentLoading,
    refetch,
    error: assignmentError,
  } = useAssignment({ assignmentId })
  const projectCategory = assignment?.category?.key ?? 'text'

  if (assignmentError) {
    return (
      <div className={styles.noAccessWrapper}>
        <img className={styles.image} src={SecureFiles} alt="no access" />
        <p>Oh no! It seems you don&apos;t have access to this file! :(</p>
      </div>
    )
  }

  if (isAssignmentLoading || editorLoading) {
    return <Spin className={styles.center} />
  }
  return (
    <>
      <div className={clsx(styles.pepperEditorWrap, styles.customScrollBar)}>
        {editorContentText?.trim() && (
          <div
            className={styles.showEditorContent}
            dangerouslySetInnerHTML={{
              __html: editorContent as string,
            }}
          />
        )}
        {assignment?.status &&
          [ASSIGNMENT_STATUS.CLI_AWA.id, ASSIGNMENT_STATUS.CLI_FIN.id].includes(
            assignment?.status
          ) && (
            <div className={styles.footerWrap}>
              <div className={styles.makeChangesHeader}>
                You are currently viewing this submission. To make changes,
                <a
                  href={`/${companyId}/document/${assignmentId}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.changesLink}
                >
                  open it in editor
                  <ArrowRight className={styles.changeIcon} />
                </a>
              </div>
              <div className={styles.pepperEditorFooter}>
                {assignment && (
                  <>
                    <div
                      className={clsx(styles.actionableArea, styles.actionPE)}
                    >
                      {assignment?.status === ASSIGNMENT_STATUS.CLI_AWA.id && (
                        <>
                          <Button
                            type="primary"
                            className={styles.approvePE}
                            onClick={() =>
                              acceptAssignmentModal.show({
                                assignmentIds: [assignmentId],
                                shouldSubmitAssignmentApproval: true,
                                companyId,
                                refetch,
                              })
                            }
                          >
                            Approve Assignment
                          </Button>
                          <Button
                            type="primary"
                            className={styles.reworkPE}
                            ghost={true}
                            onClick={() =>
                              declineAssignmentModal.show({
                                assignment: { id: assignmentId },
                                handleOnOkClick: refetch,
                                handleOnCancelClick: noop,
                                type: 'rework',
                                projectCategory,
                              })
                            }
                          >
                            Request Rework
                          </Button>

                          <PopOver
                            content={
                              <Button
                                type="text"
                                className={styles.reject}
                                onClick={() => {
                                  reworkConfirmationModal.show({
                                    assignmentId,
                                    projectCategory,
                                    refetch: { assignmentDetails: refetch },
                                  })
                                  setShouldShowReworkRejectMenu(false)
                                }}
                              >
                                Unsatisfied with the content
                              </Button>
                            }
                            visible={shouldShowReworkRejectMenu}
                            trigger="click"
                            placement="bottom"
                            overlayClassName={styles.popOver}
                          >
                            <Button
                              type="default"
                              className={styles.filterBtn}
                              onClick={() =>
                                setShouldShowReworkRejectMenu(
                                  !shouldShowReworkRejectMenu
                                )
                              }
                            >
                              <MoreHorizontal
                                size={16}
                                onClick={() =>
                                  setShouldShowReworkRejectMenu(true)
                                }
                              />
                            </Button>
                          </PopOver>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
      </div>
    </>
  )
}

export default Content
